<template>
  <transition name="ghost-overlay">
    <div
      v-if="visible"
      class="ghost-overlay"
      :style="positionStyle"
      @click.stop
    >
      <!-- Connection line to the parent ghost icon -->
      <div class="ghost-connector" />

      <!-- Header: title + the on/off action + close. Stays open until closed here (or by
           re-clicking the ghost icon) — no click-outside dismissal. -->
      <div class="ghost-header">
        <span class="ghost-title">Ghosting</span>
        <div class="ghost-header-actions">
          <div class="gc-seg" :class="{ 'is-disabled': !canToggle }">
            <button
              class="gc-seg-btn"
              :class="{ 'is-on': !ghostingOn }"
              :disabled="!canToggle"
              @click="ghostingOn && $emit('toggle-ghost')"
            >Off</button>
            <button
              class="gc-seg-btn"
              :class="{ 'is-on': ghostingOn }"
              :disabled="!canToggle"
              @click="!ghostingOn && $emit('toggle-ghost')"
            >On</button>
          </div>
          <button class="ghost-close" title="Close" @click="$emit('close')">
            <v-icon size="16">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- Gated hint: ghosting greys everything not isolated, so it needs a selection -->
      <p v-if="!canToggle" class="ghost-hint">Isolate an item first to ghost the rest.</p>

      <div class="ghost-divider" />

      <!-- Style: wireframe vs solid grey -->
      <div class="ghost-row">
        <span class="ghost-label">Style</span>
        <div class="gc-seg">
          <button
            class="gc-seg-btn"
            :class="{ 'is-on': wireframe }"
            @click="$emit('set-wireframe', true)"
          >Wireframe</button>
          <button
            class="gc-seg-btn"
            :class="{ 'is-on': !wireframe }"
            @click="$emit('set-wireframe', false)"
          >Solid</button>
        </div>
      </div>

      <!-- Solid-only controls: colour + opacity -->
      <template v-if="!wireframe">
        <div class="ghost-row">
          <span class="ghost-label">Colour</span>
          <input
            type="color"
            class="ghost-color"
            :value="color"
            @input="$emit('set-color', $event.target.value)"
          />
        </div>
        <div class="ghost-row">
          <span class="ghost-label">Opacity</span>
          <div class="ghost-opacity">
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              class="ghost-range"
              :value="opacity"
              @input="$emit('set-opacity', Number($event.target.value))"
            />
            <span class="ghost-opacity-value">{{ Math.round(opacity) }}%</span>
          </div>
        </div>
      </template>

      <div class="ghost-divider" />

      <!-- Preference: auto-show this panel whenever ghosting turns on -->
      <button
        class="ghost-autoshow"
        :class="{ 'is-on': autoShow }"
        @click="$emit('set-auto-show', !autoShow)"
      >
        <span class="ghost-autoshow-box">
          <v-icon v-if="autoShow" size="12">mdi-check</v-icon>
        </span>
        <span class="ghost-autoshow-label">Auto-show when ghosting</span>
      </button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'GhostControlOverlay',

  props: {
    visible: { type: Boolean, default: false },
    position: { type: Object, default: () => ({ x: 0, y: 0 }) },
    // Ghosting on/off — the action. Only meaningful with an active isolation.
    ghostingOn: { type: Boolean, default: false },
    // true = wireframe, false = solid grey (mirrors store.isGhostingWireframe).
    wireframe: { type: Boolean, default: true },
    color: { type: String, default: '#cad3db' },
    opacity: { type: Number, default: 100 },
    // Is anything isolated right now? Gates the on/off action.
    canToggle: { type: Boolean, default: false },
    autoShow: { type: Boolean, default: true },
  },

  computed: {
    positionStyle() {
      return {
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
// ============================================================================
// Ghost Control Overlay — shares the QuickSettings overlay chrome
// (connector, blurred gradient panel, light/dark theming, transitions).
// ============================================================================

.ghost-overlay {
  position: absolute;
  transform: translateX(-50%);
  z-index: 1001;
  min-width: 232px;
  padding: 10px;
  border-radius: var(--ds-radius-md);
  backdrop-filter: blur(16px);

  // Dark theme (default)
  background: linear-gradient(180deg, rgba(25, 30, 35, 0.98) 0%, rgba(18, 22, 26, 0.98) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);

  // Light theme
  .theme--light & {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%);
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.15),
      0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.ghost-connector {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 8px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.2) 100%);

  .theme--light & {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.15) 100%);
  }
}

// ---- Header ---------------------------------------------------------------
.ghost-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ghost-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ghost-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  border-radius: var(--ds-radius-sm);
  cursor: pointer;
  transition: background 0.15s ease;

  .v-icon {
    color: rgba(255, 255, 255, 0.5);

    .theme--light & {
      color: rgba(0, 0, 0, 0.5);
    }
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);

    .theme--light & {
      background: rgba(0, 0, 0, 0.06);
    }

    .v-icon {
      color: rgba(255, 255, 255, 0.9);

      .theme--light & {
        color: rgba(0, 0, 0, 0.8);
      }
    }
  }
}

.ghost-title {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);

  .theme--light & {
    color: rgba(0, 0, 0, 0.45);
  }
}

.ghost-hint {
  margin: 6px 0 0;
  font-size: 10px;
  line-height: 1.35;
  color: rgba(255, 180, 84, 0.85); // amber — a nudge, not an error

  .theme--light & {
    color: rgba(176, 106, 8, 0.9);
  }
}

// ---- Rows -----------------------------------------------------------------
.ghost-divider {
  height: 1px;
  margin: 10px 0;
  background: rgba(255, 255, 255, 0.08);

  .theme--light & {
    background: rgba(0, 0, 0, 0.07);
  }
}

.ghost-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;

  & + & {
    margin-top: 6px;
  }
}

.ghost-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);

  .theme--light & {
    color: rgba(0, 0, 0, 0.65);
  }
}

// ---- Segmented control (on/off + wireframe/solid) -------------------------
.gc-seg {
  display: inline-flex;
  padding: 2px;
  border-radius: var(--ds-radius-sm);
  background: rgba(255, 255, 255, 0.06);

  .theme--light & {
    background: rgba(0, 0, 0, 0.05);
  }

  &.is-disabled {
    opacity: 0.4;
  }
}

.gc-seg-btn {
  appearance: none;
  border: none;
  background: transparent;
  padding: 4px 10px;
  border-radius: calc(var(--ds-radius-sm) - 1px);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.5);
  transition: background 0.15s ease, color 0.15s ease;

  .theme--light & {
    color: rgba(0, 0, 0, 0.5);
  }

  &:hover:not(:disabled):not(.is-on) {
    color: rgba(255, 255, 255, 0.8);

    .theme--light & {
      color: rgba(0, 0, 0, 0.8);
    }
  }

  &.is-on {
    background: var(--v-primary-base);
    color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  }

  &:disabled {
    cursor: not-allowed;
  }
}

// ---- Colour swatch --------------------------------------------------------
.ghost-color {
  width: 44px;
  height: 24px;
  padding: 0 2px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--ds-radius-sm);
  background: transparent;
  cursor: pointer;

  .theme--light & {
    border-color: rgba(0, 0, 0, 0.15);
  }
}

// ---- Opacity slider -------------------------------------------------------
.ghost-opacity {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ghost-range {
  width: 110px;
  accent-color: var(--v-primary-base);
  cursor: pointer;
}

.ghost-opacity-value {
  font-size: 11px;
  font-weight: 600;
  min-width: 34px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--v-primary-base);
}

// ---- Auto-show preference -------------------------------------------------
.ghost-autoshow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.ghost-autoshow-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  transition: background 0.15s ease, border-color 0.15s ease;

  .theme--light & {
    border-color: rgba(0, 0, 0, 0.25);
  }

  .v-icon {
    color: #fff !important;
  }
}

.ghost-autoshow.is-on .ghost-autoshow-box {
  background: var(--v-primary-base);
  border-color: var(--v-primary-base);
}

.ghost-autoshow-label {
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);

  .theme--light & {
    color: rgba(0, 0, 0, 0.6);
  }
}

// ---- Transitions (match sibling overlays) ---------------------------------
.ghost-overlay-enter-active {
  animation: ghostOverlayIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.ghost-overlay-leave-active {
  animation: ghostOverlayOut 0.12s ease-out;
}

@keyframes ghostOverlayIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

@keyframes ghostOverlayOut {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px) scale(0.98);
  }
}
</style>
